import { memo, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { IconSetDefinitions } from '@hotjar/hotsauce';
import { Link, tokens } from '@hotjar/hotsauce';
import { useIsConsoleAvailable } from '@hotjar/recordings/src/features/RecordingsPage/hooks/useIsConsoleAvailable';
import type { ActionFilter } from '@hotjar/recordings/src/features/PlayerPage/components/EventBoxHolder/types';
import {
  BehaviorNameEnum,
  EventTypeEnum,
  HiddenEventTypeEnum,
  type EventMarker,
} from '@hotjar/webapp/features/Playbacks/types';
import { Title } from '../styles';
import { Container, StyledTileContainer, StyledText } from './styles';

import { Tile } from './tiles/Tile';
import { ErrorTile } from './tiles/ErrorTile';
import { UpsellErrorTile } from './tiles/UpsellErrorTile';

type Props = {
  eventMarkers: EventMarker[];
  onActionClick: (type?: ActionFilter) => void;
};

const mapEventMarkers = (eventMarkers: EventMarker[]): Record<ActionFilter, EventMarker[]> =>
  eventMarkers.reduce((acc: Record<ActionFilter, EventMarker[]>, marker) => {
    const type: ActionFilter =
      marker.type !== HiddenEventTypeEnum.AUTOTAG_RECORDING
        ? (marker.type as ActionFilter)
        : (marker.name as ActionFilter);

    return { ...acc, [type]: [...(acc[type] || []), marker] };
  }, {} as Record<ActionFilter, EventMarker[]>);

const InternalUserActionsRow = ({ onActionClick, eventMarkers }: Props): JSX.Element => {
  const { t } = useTranslation();
  const isConsoleAvailable = useIsConsoleAvailable();

  const errorTile = !isConsoleAvailable ? <UpsellErrorTile /> : <ErrorTile />;

  const mappedMarkers = useMemo(() => mapEventMarkers(eventMarkers), [eventMarkers]);
  const renderTile = useCallback(
    (icon: keyof IconSetDefinitions, color: string, type: ActionFilter) => (
      <Tile
        icon={icon}
        color={color}
        value={mappedMarkers[type]?.length || 0}
        title={t(`common:player.actions-list.action-type.widget.${type.replace('_', '-')}`, {
          count: 0,
        })}
        onClick={mappedMarkers[type] ? () => onActionClick(type) : undefined}
      />
    ),
    [mappedMarkers, onActionClick, t],
  );

  return (
    <Container>
      <Title>{t('common:player.side-panel.user-actions.title')}</Title>

      <StyledTileContainer>
        {renderTile('cursor_click', tokens.color.cobalt_200, EventTypeEnum.MOUSE_CLICK)}
        {renderTile('text_field_tall', tokens.color.mint_300, EventTypeEnum.KEY_PRESS)}
        {renderTile('rage_click', tokens.color.peach_300, BehaviorNameEnum.RAGE_CLICK)}
        {renderTile('uturn', tokens.color.noon_300, BehaviorNameEnum.U_TURN)}
        {renderTile('surveys', tokens.color.lilac_300, BehaviorNameEnum.POLL)}
        {renderTile('feedback', tokens.color.lilac_300, BehaviorNameEnum.FEEDBACK)}

        {errorTile}

        {renderTile('lightning', tokens.color.aqua_300, EventTypeEnum.TAG_RECORDING)}
      </StyledTileContainer>

      <StyledText as="p" textType="caption">
        <Link as="button" kind="inline" onClick={() => onActionClick()}>
          {t('common:player.side-panel.user-actions.see-all', { count: eventMarkers.length })}
        </Link>
      </StyledText>
    </Container>
  );
};

export const UserActionsRow = memo(InternalUserActionsRow);
